/**
 * Title-case a hospital name for display.
 *
 * Many chargemaster filings come in ALL CAPS (HACKETTSTOWN MEDICAL CENTER) which
 * looks shouty in journalist-facing copy. This util:
 * - Converts ALL-CAPS strings to Title Case
 * - Preserves already-mixed-case names (Cedars-Sinai stays as-is)
 * - Keeps known acronyms uppercase (UAB, NYU, UCSF, MS-DRG, USA, ER, ICU, etc.)
 * - Preserves common ampersand/dash patterns
 */

const KEEP_UPPER = new Set([
  "UAB","NYU","UCLA","UCSF","UCSD","UCD","UCI","UCR","UCSB","USC","MIT",
  "USA","UK","DC","NYC","LA","SF","STL","BWI",
  "ER","ICU","NICU","PICU","ED","OR","OB","GYN","ENT","ADHD",
  "CT","MRI","X-RAY","PET",
  "AHS","HCA","HSHS","HHC","NHRMC","UPMC","CHKD","SSM","KP","HCSC",
  "DRG","CPT","HCPCS","NDC","REV","CDM","ICD","CDT",
  "VA","DOD","CMS","NIH","CDC","FDA","HHS",
  "LLC","INC","PC","PA","LLP","LP",
  "I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII",
  "OF","THE","AND","FOR","AT","TO","IN","ON","BY","AS","DE","LA","DEL","LE",
]);

const KEEP_LOWER = new Set(["of","the","and","for","at","to","in","on","by","as","de","la","del","le"]);

function titleCaseWord(w: string, isFirst: boolean, isLast: boolean): string {
  if (!w) return w;
  // Keep all-numeric tokens
  if (/^\d+$/.test(w)) return w;
  // Mixed case (already proper-cased) — leave as is
  if (/[a-z]/.test(w) && /[A-Z]/.test(w) && !w.match(/^[A-Z]+$/)) return w;

  const upper = w.toUpperCase();
  if (KEEP_UPPER.has(upper)) {
    // Lowercase joining words mid-name
    if (KEEP_LOWER.has(upper.toLowerCase()) && !isFirst && !isLast) return upper.toLowerCase();
    return upper;
  }

  // Hyphenated like "Cedars-Sinai" or "MS-DRG"
  if (w.includes("-")) {
    return w.split("-").map((p) => titleCaseWord(p, false, false)).join("-");
  }

  // Apostrophes like "St. Joseph's" — preserve apostrophe + downcase rest
  if (w.includes("'") || w.includes("’")) {
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }

  // Default: First letter upper, rest lower
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

export function titleCaseHospital(name: string | null | undefined): string {
  if (!name) return "";
  // If the input has any lowercase letters AND mixed case, assume it's already correct.
  if (/[a-z]/.test(name) && /[A-Z]/.test(name) && name !== name.toUpperCase()) {
    return name;
  }
  const tokens = name.trim().split(/\s+/);
  return tokens
    .map((t, i) => titleCaseWord(t, i === 0, i === tokens.length - 1))
    .join(" ");
}

/**
 * Title-case a city name for display. Plain per-word capitalization —
 * "EL PASO" → "El Paso", "LA JOLLA" → "La Jolla". (Cities don't carry the
 * acronym/joining-word complexity hospital names do; titleCaseHospital's
 * KEEP_UPPER list would wrongly yield "LA Jolla".)
 */
export function titleCaseCity(city: string | null | undefined): string {
  if (!city) return "";
  // Already mixed-case → assume correct.
  if (/[a-z]/.test(city) && /[A-Z]/.test(city) && city !== city.toUpperCase()) {
    return city;
  }
  return city
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}
